import { useState, useEffect, useMemo } from 'react';

import {
  cleanBookTitle,
  getChapterFromIdx,
  getListOfStartChapters,
  getListOfEndChapters,
} from '../Contexts/DiffitBooksContext';
import { Mixpanel } from '../../../services/Mixpanel';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import BasicModal from '../../../components/modals/BasicModal';
import { Button } from '../../../components/library/button';
import { BOOKS_ID, TABS, usePacketInput } from '../Contexts/PacketInputContext';
import { useGenerateResources } from '../Hooks/useGenerateResource';
import { Dropdown } from '../Components/Dropdown';
import Image from '../../../components/Image';

export const PossibleBookMatchModal = ({ showModal, setShowModal, book }) => {
  const [startChapterIdx, setstartChapterIdx] = useState(-1);
  const [endChapterIdx, setendChapterIdx] = useState(-1);
  const { topic } = usePacketInput();
  const { handleGenerateResources } = useGenerateResources();

  useEffect(() => {
    Mixpanel.track('Literally Anything Book followup modal shown', { book, topic });
  }, []);

  const startChapterOptions = useMemo(
    () => (book ? getListOfStartChapters(book.chapters, endChapterIdx) : ['Select a chapter']),
    [book, endChapterIdx]
  );

  const endChapterOptions = useMemo(
    () => (book ? getListOfEndChapters(book.chapters, startChapterIdx) : ['Select a chapter']),
    [book, startChapterIdx]
  );

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={(show) => {
        Mixpanel.track('Literally Anything Book followup modal closed without generating', {
          book,
          topic,
        });
        setShowModal(show);
      }}
      title={''}
      overflowY='visible'
      maxWidth='max-w-xl'>
      <div className='w-full px-4'>
        <div className='flex-col justify-start items-center gap-[26px] inline-flex w-full'>
          <div className='text-center text-gray-500 text-xl font-normal '>
            Looking for resources for this book?
          </div>
          <div className='w-full px-4 py-6 border-t border-b border-gray-300 flex-col justify-start items-start gap-[26px] flex'>
            <div className='justify-start items-center gap-[26px] inline-flex'>
              <Image className='w-11 h-16' src={book.cover} alt={book.title} />
              <div>
                <span className='text-gray-900 text-lg font-bold '>
                  {cleanBookTitle(book.title)}
                  <br />
                </span>
                <span className='text-gray-700 text-sm font-medium '>by {book.author}</span>
              </div>
            </div>
            <div className='flex justify-between gap-x-6 w-full'>
              <div className='flex flex-col w-full '>
                <input
                  type='hidden'
                  form='generate-form'
                  name='startChapterIdx'
                  value={startChapterIdx}
                />
                <p className={`text-gray-600 text-xs text-left pb-2 font-semibold`}>
                  Start Chapter
                </p>
                <Dropdown
                  openDirection='up'
                  placeholder={'Select chapter'}
                  name='chapter-start'
                  optionList={startChapterOptions}
                  extraClasses='border-gray-400 border'
                  premiumRow={{
                    show: true,
                    message: 'Get unlimited access',
                    link: 'https://web.diffit.me/plans',
                    type: 'premium',
                    location: 'Start Chapter Dropdown',
                  }}
                  selected={getChapterFromIdx(book, startChapterIdx)}
                  setSelected={(selected) => {
                    let selectedIndex = selected.value;
                    const newStartChapterIdx = selectedIndex - 1;
                    Mixpanel.track(
                      'Selected Start Chapter in Literally Anything Book followup modal',
                      {
                        chapter: newStartChapterIdx,
                      }
                    );
                    setstartChapterIdx(newStartChapterIdx);
                    if (endChapterIdx === -1) {
                      setendChapterIdx(newStartChapterIdx);
                    }
                  }}
                />
              </div>
              <div className='flex flex-col w-full'>
                <input
                  type='hidden'
                  form='generate-form'
                  name='endChapterIdx'
                  value={endChapterIdx}
                />

                <p className={`text-gray-600 text-xs text-left pb-2 font-semibold`}>End Chapter</p>
                <Dropdown
                  openDirection='up'
                  name='chapter-end'
                  extraClasses='border-gray-400 border'
                  optionList={endChapterOptions}
                  selected={getChapterFromIdx(book, endChapterIdx)}
                  setSelected={(selected) => {
                    let selectedIndex = selected.value;
                    const newEndChapterIdx = selectedIndex - 1;
                    Mixpanel.track(
                      'Selected End Chapter in Literally Anything Book followup modal',
                      {
                        chapter: newEndChapterIdx,
                      }
                    );
                    setendChapterIdx(newEndChapterIdx);
                  }}
                  placeholder={'Select chapter'}
                  premiumRow={{
                    show: true,
                    message: 'Get unlimited access',
                    link: 'https://web.diffit.me/plans',
                    type: 'premium',
                    location: 'End Chapter Dropdown',
                  }}
                />
              </div>
            </div>
          </div>
          <div className='w-full mt-2'>
            <Button
              onClick={() => {
                if (startChapterIdx === -1 || endChapterIdx === -1) {
                  Mixpanel.track(
                    'Pressed Confirm Book in Literally Anything Book followup modal without selecting chapters',
                    { book, topic }
                  );
                  alert('Please select chapters');
                  return;
                }
                const startChapter = getChapterFromIdx(book, startChapterIdx);
                const endChapter = getChapterFromIdx(book, endChapterIdx);
                handleGenerateResources({
                  inputType: TABS.find((tab) => tab.id === BOOKS_ID),
                  book,
                  startChapter: { name: startChapter, value: startChapterIdx + 1 },
                  endChapter: { name: endChapter, value: endChapterIdx + 1 },
                });
                setShowModal(false);
              }}
              variant={'primary'}>
              Confirm Book
            </Button>
          </div>
          <button
            onClick={() => {
              setShowModal(false);
              Mixpanel.track('Selected Proceed Anyway in Literally Anything Book followup modal', {
                book: book,
                topic,
              });
              handleGenerateResources({ proceedFromBookMatch: true });
            }}
            className='cursor-pointer text-emerald-600 hover:text-emerald-500 text-base self-center '>
            <div className='flex w-fit items-center justify-center text-md  '>
              <span>No, proceed with "</span>
              <span className='font-semibold truncate max-w-[200px]'>{topic}</span>
              <span>" as a topic</span>
              <ArrowRightIcon className='h-5 w-5 ml-1' />
            </div>
          </button>
        </div>
      </div>
    </BasicModal>
  );
};
